import React, { useEffect, useMemo, useRef, useState } from 'react'
import InvoiceSummary from '../../HourlyInvoice/InvoiceSummary'
import InvoicePreview from '../../HourlyInvoice/InvoicePreview'
import { useHourlyUpdateInvoice } from './context/HourlyUpdateInvoiceContext'
import { buildPreviewProps, buildPreviewValues } from './previewAdapter'
import { partyLabelFor } from './partyLabel'

/**
 * Step 3 — the review, laid out exactly like the FIXED editor's step 3
 * (pages/UpdateInvoice.jsx): a full-screen `bg-gray-50` takeover, a
 * `max-w-[1600px]` wrapper, `grid-cols-12` with the summary in `col-span-4`
 * and the invoice document in `col-span-8`, both on `bg-gray-100 rounded`, and
 * a `col-span-12` action bar with "Back to edit" and "Update Invoice".
 *
 * Both panes are the REAL hourly components — `HourlyInvoice/InvoiceSummary`
 * and `HourlyInvoice/InvoicePreview`, the same pair the hourly CREATE flow and
 * the fixed editor render. So the document approved here is what the client
 * receives, rather than a lookalike that drifts the first time the invoice
 * layout changes.
 *
 * There is NO context provider and NO synthesised `setFieldValue`: CM's panes
 * take plain props and never write back.
 *
 * Editing several parties renders one document each, switched above the
 * document: they are separate invoices with their own recipient and total, not
 * a combined statement.
 */

const fmtMoney = (n) =>
    Number(n || 0).toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    })

const ChevronDown = ({ className = '' }) => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" className={className}>
        <path d="M5 7l5 6 5-6H5z" />
    </svg>
)

const CheckIcon = () => (
    <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <polyline points="20 6 9 17 4 12" />
    </svg>
)

export default function Step3({ onExit }) {
    const {
        caseInfo,
        mediatorRecord,
        targetRows,
        itemsByInvoice,
        dueDateByInvoice,
        notesByInvoice,
        clientInfoByInvoice,
        subtotalFor,
        breakdownFor,
        editScope,
        results,
        submitting,
        submit,
        allSucceeded,
        failedCount,
        handleBack,
        setCurrentStep,
        navigateToCase,
    } = useHourlyUpdateInvoice()

    const [activeId, setActiveId] = useState(String(targetRows[0]?.id ?? ''))
    const active = targetRows.find((r) => String(r.id) === activeId) || targetRows[0]

    /* A selection change upstream can strand activeId on a row no longer being
     * edited, which would leave the review showing a document that is not going
     * to be sent. */
    useEffect(() => {
        if (targetRows.length && !targetRows.some((r) => String(r.id) === activeId)) {
            setActiveId(String(targetRows[0].id))
        }
    }, [targetRows, activeId])

    const [plaintiffOpen, setPlaintiffOpen] = useState(false)
    const [defendantOpen, setDefendantOpen] = useState(false)
    const plaintiffRef = useRef(null)
    const defendantRef = useRef(null)

    useEffect(() => {
        const onDown = (e) => {
            if (plaintiffRef.current && !plaintiffRef.current.contains(e.target)) {
                setPlaintiffOpen(false)
            }
            if (defendantRef.current && !defendantRef.current.contains(e.target)) {
                setDefendantOpen(false)
            }
        }
        document.addEventListener('mousedown', onDown)
        return () => document.removeEventListener('mousedown', onDown)
    }, [])

    /* THE TAB FIGURE.
     *
     * `row.amount` is the frozen PRE-EDIT column, so a strip built from it
     * advertises $375 beside a document the same screen is about to issue for
     * $500. Recomputed from the edited ledger and each party's own stored share
     * — the same resolution the backend bills from. */
    const ledgerTotal = subtotalFor(targetRows[0]?.id)
    const totalFor = (row) => breakdownFor(row, ledgerTotal).total
    const optionLabel = (row) => partyLabelFor(row, caseInfo, mediatorRecord)

    const breakdown = useMemo(
        () => (active ? breakdownFor(active, ledgerTotal) : null),
        [active, breakdownFor, ledgerTotal]
    )

    const previewValues = useMemo(() => {
        if (!active) return null
        return buildPreviewValues({
            row: active,
            items: itemsByInvoice[active.id] || [],
            dueDate: dueDateByInvoice[active.id],
            notes: notesByInvoice[active.id],
            caseInfo,
            clientInfo: clientInfoByInvoice[active.id],
            /* Passed ONLY in party scope. In session scope the stored share
             * must be resolved from the row, or a 3-party $900 edit would
             * preview $900 on every tab while Stripe bills $300 each. */
            shareOverride:
                editScope === 'party' && breakdown
                    ? { share: 1, adminInAmount: breakdown.adminInAmount }
                    : undefined,
        })
    }, [
        active,
        itemsByInvoice,
        dueDateByInvoice,
        notesByInvoice,
        caseInfo,
        clientInfoByInvoice,
        editScope,
        breakdown,
    ])

    const previewProps = useMemo(() => {
        if (!active || !breakdown) return null
        return buildPreviewProps({
            row: active,
            caseInfo,
            clientInfo: clientInfoByInvoice[active.id],
            mediatorRecord,
            breakdown,
        })
    }, [active, caseInfo, clientInfoByInvoice, mediatorRecord, breakdown])

    /* Reachable only if the selection was emptied from step 1 after reaching
     * the review. Rendering nothing would leave a blank page over the case, so
     * send the user back rather than stranding them. */
    if (!active || !previewValues || !previewProps) {
        return (
            <div className="fixed inset-0 z-[60] overflow-auto bg-gray-50">
                <div className="p-6 max-w-[1600px] mx-auto">
                    <div className="bg-white border rounded p-6 flex flex-col items-center gap-3 text-center">
                        <p className="text-sm text-gray-600">
                            No invoice is selected for this edit any more.
                        </p>
                        <button
                            type="button"
                            onClick={() => setCurrentStep(1)}
                            className="px-4 py-2 text-sm font-medium text-white rounded-md"
                            style={{ backgroundColor: '#115E59' }}
                        >
                            Back to Select Party
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    const recipient = previewProps.recipient

    /* InvoicePreview resolves its recipient in a useState initializer and only
     * re-resolves through an effect keyed on client_id. Keying the panes on the
     * recipient's own contents means an edited name or address repaints instead
     * of leaving the previous party's Bill To on screen. */
    const paneKey = [
        active.id,
        recipient.name,
        recipient.email,
        recipient.address,
        recipient.phone,
    ].join('|')

    const primaryLabel = allSucceeded
        ? 'Done'
        : results
          ? `Retry ${failedCount} invoice${failedCount === 1 ? '' : 's'}`
          : targetRows.length > 1
            ? `Update ${targetRows.length} Invoices`
            : 'Update Invoice'

    /* TWO tabs — Plaintiff and Defendant — each opening a dropdown of that
     * side's parties, exactly as the live preview does it. A tab per party does
     * not scale: four parties give four cramped tabs, and additional parties on
     * a side have nowhere to go.
     *
     * Collapse rule copied verbatim from InvoicePreview: only when BOTH sides
     * hold exactly one party do the tabs become plain buttons naming that
     * party, so a side with two parties forces both columns into dropdown form
     * and the pair stays symmetrical. */
    const plaintiffRows = targetRows.filter((r) => r.side === 'plaintiff')
    const defendantRows = targetRows.filter((r) => r.side === 'defendant')
    const bothSingle = plaintiffRows.length === 1 && defendantRows.length === 1

    const SideTab = ({ side, rows, containerRef, isOpen, setOpen }) => {
        if (rows.length === 0) return null
        const isActive = active.side === side
        const label = side === 'plaintiff' ? 'Plaintiff' : 'Defendant'
        const btnCls = `w-full px-4 py-2 rounded-md text-sm font-medium border shadow-md ${
            isActive
                ? 'bg-teal-800 text-white border-teal-800'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
        }`
        const widthCls =
            plaintiffRows.length > 0 && defendantRows.length > 0
                ? 'relative w-1/2'
                : 'relative w-full'

        if (bothSingle) {
            return (
                <div className={widthCls}>
                    <button
                        type="button"
                        onClick={() => setActiveId(String(rows[0].id))}
                        className={btnCls}
                    >
                        <div className="w-full flex justify-center">
                            <div className="flex items-center gap-2">
                                <span>{optionLabel(rows[0])}</span>
                                <span className={isActive ? 'text-white/70' : 'text-gray-400'}>
                                    ${fmtMoney(totalFor(rows[0]))}
                                </span>
                            </div>
                        </div>
                    </button>
                </div>
            )
        }

        return (
            <div className={widthCls} ref={containerRef}>
                <button type="button" onClick={() => setOpen(!isOpen)} className={btnCls}>
                    <div className="flex justify-between items-center">
                        <div className="w-full flex justify-center">
                            <span>{label}</span>
                        </div>
                        <ChevronDown
                            className={`transition-transform duration-200 ${
                                isOpen ? 'rotate-180' : ''
                            }`}
                        />
                    </div>
                </button>
                {isOpen && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-50">
                        {rows.map((r) => (
                            <button
                                type="button"
                                key={r.id}
                                onClick={() => {
                                    setActiveId(String(r.id))
                                    setOpen(false)
                                }}
                                className="capitalize text-gray-500 w-full text-left px-4 py-2 hover:bg-gray-100 first:rounded-t-md last:rounded-b-md focus:outline-none"
                            >
                                <div className="flex justify-between items-center gap-2">
                                    <span className="truncate">
                                        {optionLabel(r)}{' '}
                                        <span className="text-gray-400">
                                            ${fmtMoney(totalFor(r))}
                                        </span>
                                    </span>
                                    {String(r.id) === String(active.id) && <CheckIcon />}
                                </div>
                            </button>
                        ))}
                    </div>
                )}
            </div>
        )
    }

    return (
        <div className="fixed inset-0 z-[60] overflow-auto bg-gray-50">
            <div className="p-6 max-w-[1600px] mx-auto space-y-4">
                <button
                    type="button"
                    onClick={onExit || navigateToCase}
                    className="text-sm text-[#115E59] mb-4 hover:underline"
                >
                    ← Back to Case
                </button>

                {/* Per-invoice outcomes. Shown for EVERY target, not just the
                    visible one — a half-applied multi-party update must not be
                    hidden behind a tab nobody opens. */}
                {results && (
                    <div className="space-y-2">
                        {targetRows.map((row) => {
                            const result = results.find(
                                (r) => String(r.hourly_invoice_id) === String(row.id)
                            )
                            if (!result) return null
                            return (
                                <div
                                    key={row.id}
                                    className={`flex items-start gap-2 rounded-lg border px-3 py-2 ${
                                        result.success
                                            ? 'border-teal-100 bg-teal-50'
                                            : 'border-red-100 bg-red-50'
                                    }`}
                                >
                                    <span
                                        className={`mt-0.5 shrink-0 ${
                                            result.success ? 'text-teal-800' : 'text-red-500'
                                        }`}
                                    >
                                        {result.success ? <CheckIcon /> : '!'}
                                    </span>
                                    <span
                                        className={`text-xs leading-relaxed ${
                                            result.success ? 'text-gray-700' : 'text-red-800'
                                        }`}
                                    >
                                        <b>
                                            {partyLabelFor(row, caseInfo, mediatorRecord)}
                                        </b>
                                        {' — '}
                                        {result.success
                                            ? `reissued as ${
                                                  result.invoice_number ||
                                                  'a revised invoice'
                                              } for $${fmtMoney(result.amount)}.${
                                                  result.notified === false
                                                      ? ' The email could not be sent — tell the party directly.'
                                                      : ' The party has been emailed.'
                                              }`
                                            : result.reason}
                                    </span>
                                </div>
                            )
                        })}
                    </div>
                )}

                <div className="grid grid-cols-12 gap-4">
                    <div className="col-span-4 bg-gray-100 rounded">
                        <InvoiceSummary
                            key={paneKey}
                            values={previewValues}
                            onEditRecipient={() => setCurrentStep(1)}
                            onEditDetails={() => setCurrentStep(2)}
                            plaintiff={null}
                            defendant={null}
                            additionalPlaintiffParties={[]}
                            additionalDefendantParties={[]}
                            selectedRecipient={recipient}
                            /* One document, one recipient — never the combined
                               two-column recipient block. */
                            isAll={false}
                            adminFeeAmount={previewProps.adminFee}
                            /* The reissue re-bills whatever admin fee this row
                               carries, so it is never "already collected"; the
                               added-vs-deducted wording below it is what tells
                               the two cases apart. */
                            adminFeeCollected={false}
                            isAdminFeeAddedInLineItem={
                                previewProps.isAdminFeeAddedInLineItem
                            }
                        />
                    </div>

                    <div className="col-span-8 space-y-3">
                        {targetRows.length > 1 && (
                            <div className="flex justify-between gap-2">
                                <SideTab
                                    side="plaintiff"
                                    rows={plaintiffRows}
                                    containerRef={plaintiffRef}
                                    isOpen={plaintiffOpen}
                                    setOpen={setPlaintiffOpen}
                                />
                                <SideTab
                                    side="defendant"
                                    rows={defendantRows}
                                    containerRef={defendantRef}
                                    isOpen={defendantOpen}
                                    setOpen={setDefendantOpen}
                                />
                            </div>
                        )}
                        <div className="bg-gray-100 rounded">
                            <InvoicePreview
                                key={paneKey}
                                values={previewValues}
                                mediator={previewProps.mediator}
                                caseData={previewProps.caseData}
                                /* Empty on purpose: each document is a single
                                   recipient, so the preview's own side toggle
                                   stays hidden and the switcher above owns the
                                   party choice. */
                                plaintiffOptions={[]}
                                defendantOptions={[]}
                                initialClient={recipient}
                                selectClientById={() => recipient}
                                splitFactor={previewProps.splitFactor}
                                perPartyShare={previewProps.perPartyShare}
                                perPartyPercentage={previewProps.perPartyPercentage}
                                processingFeeToClient={previewProps.processingFeeToClient}
                                adminFee={previewProps.adminFee}
                                isAdminFeeAddedInLineItem={
                                    previewProps.isAdminFeeAddedInLineItem
                                }
                            />
                        </div>
                    </div>

                    <div className="col-span-12 flex justify-between bg-white border p-4 rounded">
                        <button
                            type="button"
                            onClick={handleBack}
                            disabled={submitting}
                            className="text-sm text-gray-600 hover:text-gray-900 inline-flex items-center gap-1 disabled:opacity-50"
                        >
                            ← Back to edit
                        </button>
                        <button
                            type="button"
                            disabled={submitting}
                            onClick={allSucceeded ? navigateToCase : submit}
                            className="px-4 py-2 text-sm font-medium text-white rounded-md disabled:opacity-50"
                            style={{ backgroundColor: '#115E59' }}
                        >
                            {submitting ? 'Updating…' : primaryLabel}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}
